import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Star,
  Email,
  CalendarToday,
  Phone,
  Close,
  Download,
  ThumbUp,
  ThumbDown,
  AutoAwesome, // Icon for AI
  Circle,
  CheckCircleOutline,
  Edit,
  Badge, // Icon for Internal (Employee Badge)
  Public,
  Event,
} from "@mui/icons-material";
import CalendarComponent from "components/MeetingCalendar";
import TimeDialog from "components/CalendarTimeDialog";

// Replace with your actual Button component import or use MUI Button
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import { downloadApplicantCV } from "services/api";
import { updateApplicantStatus } from "services/api";
import { useAuth } from "context/authContext";

const ApplicantDialog = ({
  open,
  applicant,
  questions,
  onClose,
  onStatusChange,
  canEdit,
  meetings,
  employeeId,
  allMeetings: initialMeetings,
  empRequestedBy,
}) => {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [meetingsCount, setMeetingsCount] = useState(0);
  const [meetingsAll, setMeetingsAll] = useState(initialMeetings ?? []);
  const { employee } = useAuth();

  console.log("Applicant::", applicant);
  const [filteredMeetingsApplicant, setFilteredMeetingsApplicant] = useState([]);

  // 2. Define Status Options with Colors
  const statuses = [
    {
      value: "Pending Review",
      label: t("admin.applicants.filters.status_pending"),
      color: "info", // Blue
    },
    {
      value: "Shortlisted",
      label: t("admin.applicants.filters.status_shortlisted"),
      color: "warning", // Orange
    },
    {
      value: "Accepted",
      label: t("admin.applicants.filters.status_accepted"),
      color: "success", // Green
    },
    {
      value: "Rejected",
      label: t("admin.applicants.filters.status_rejected"),
      color: "error", // Red
    },
    {
      value: "No Show",
      label: t("admin.applicants.filters.status_no_show"),
      color: "error", // similar severity to rejected
    },
    {
      value: "Excused",
      label: t("admin.applicants.filters.status_excused"),
      color: "info", // neutral/acceptable
    },
    {
      value: "Rescheduled",
      label: t("admin.applicants.filters.status_rescheduled"),
      color: "warning", // needs attention
    },
    {
      value: "Cancelled",
      label: t("admin.applicants.filters.status_cancelled"),
      color: "info", // neutral/closed
    },
    {
      value: "Pipeline",
      label: t("admin.applicants.filters.status_pipeline"),
      color: "info", // ongoing process
    },
    {
      value: "Position Closed",
      label: t("admin.applicants.filters.status_position_closed"),
      color: "error", // final state
    },
  ];

  useEffect(() => {
    if (!applicant || !meetingsAll?.length) {
      setFilteredMeetingsApplicant([]);
      setMeetingsCount(0);
      return;
    }

    const result = meetingsAll.filter(
      (meeting) => Number(meeting.applicationId) === Number(applicant.id)
    );

    setFilteredMeetingsApplicant(result);
    setMeetingsCount(result.length);
  }, [meetingsAll, applicant]);

  if (!applicant) return null;

  const getStatusColor = (status) => {
    const found = statuses.find((s) => s.value === status);
    return found ? found.color : "default";
  };

  const handleDownloadCv = async () => {
    setDownloadLoading(true);
    await downloadApplicantCV(applicant.id, applicant.fullName);
    setDownloadLoading(false);
  };

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  const handleSelectSlot = (slotInfo) => {
    setSelectedDate(slotInfo.start);
    setAnchorEl(slotInfo.actionTarget || document.body); // show menu
    setTimeDialogOpen(true);
  };

  const handleChangeStatus = async (newStatus) => {
    handleStatusClose(); // Close menu immediately

    if (newStatus === applicant.status) return; // No change needed

    try {
      setStatusLoading(true);

      const res = await updateApplicantStatus(applicant.id, newStatus);

      // Notify parent to update the list locally
      if (onStatusChange && res != "error") {
        onStatusChange(applicant.id, newStatus);
      }
    } catch (error) {
      console.error("Failed to update status", error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSetMeeting = () => {
    setCalendarOpen(true);
  };

  const handleCloseCalendar = () => {
    setCalendarOpen(false);
  };

  // 1. Helper: Parse Semicolon lists
  const parseList = (text) => {
    if (!text) return [];
    return text
      .split(";")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  // 2. Helper: Parse AI Reasoning Steps
  const renderReasoning = (text) => {
    if (!text)
      return (
        <Typography variant="body2" color="text.secondary">
          {t("admin.applicants.dialog.sections.no_reasoning")}
        </Typography>
      );

    // FIX: Split by regex to catch both "\n" (actual newline) and "\\n" (escaped newline string)
    const lines = text.split(/\\n|\n/).filter((line) => line.trim().length > 0);

    return (
      <Stack spacing={2}>
        {lines.map((line, index) => {
          // Clean the line first
          const cleanLine = line.trim();

          // Regex explanation:
          // ^(Step \d+[^.:]*[.:])  -> Capture "Step" + number + anything until a dot or colon
          // \s*                    -> Ignore spaces after the colon/dot
          // (.*)                   -> Capture the rest as content
          const match = cleanLine.match(/^(\d+[^.:]*[.:])\s*(.*)/i);

          if (match) {
            const title = match[1]; // e.g. "Step 1: Job Data Analysis."
            const content = match[2]; // e.g. "The position is for..."

            return (
              <Box key={index} display="flex" alignItems="flex-start">
                <CheckCircleOutline
                  color="primary"
                  fontSize="small"
                  sx={{ mt: 0.5, mr: 1.5, minWidth: "24px" }}
                />
                <Box>
                  {/* <Typography variant="subtitle2" color="text.primary" fontWeight="bold">
                    {title}
                  </Typography> */}
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {content}
                  </Typography>
                </Box>
              </Box>
            );
          } else {
            // Fallback for lines that don't match "Step X"
            return (
              <Box key={index} display="flex" alignItems="flex-start">
                <Circle sx={{ fontSize: 8, mt: 1, mr: 2, color: "grey.400" }} />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {cleanLine}
                </Typography>
              </Box>
            );
          }
        })}
      </Stack>
    );
  };

  const strengthsList = parseList(applicant.strengths);
  const weaknessesList = parseList(applicant.weaknesses);

  const allTimes = Array.from({ length: 24 * 60 }, (_, i) => {
    const h = Math.floor(i / 60);
    const m = i % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  });

  // Merge Questions with Answers based on ID
  const renderQnA = () => {
    // Sort questions by orderIndex first
    const sortedQuestions = [...questions].sort((a, b) => a.orderIndex - b.orderIndex);

    return sortedQuestions?.map((q) => {
      // Map based on questionId.
      // Note: Ensure your backend DTO uses 'answerText' or 'answer' consistently.
      const answerObj = applicant.answers.find((a) => a.questionId === q.id);
      const answerText = answerObj
        ? answerObj.answerText || answerObj.answer
        : t("admin.applicants.dialog.sections.no_answer");

      return (
        <Box key={q.id} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {q.questionText}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
              {answerText}
            </Typography>
          </Paper>
        </Box>
      );
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      {/* HEADER */}
      <DialogTitle
        sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {applicant.fullName}
          </Typography>
          <Chip
            label={
              t(`admin.talentAcquisition.statusInMeeting.${applicant.status}`) || applicant.status
            }
            color={getStatusColor(applicant.status)}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, fontWeight: "bold" }}
          />
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent dividers>
        {/* TOP SECTION: Contact Chips */}
        <Box display="flex" gap={1} mb={3} flexWrap="wrap">
          {/* 1. Internal/External Status Chip */}
          <Chip
            icon={
              String(applicant.phone || "").trim() ? (
                <Public fontSize="small" />
              ) : (
                <Badge fontSize="small" />
              )
            }
            label={
              String(applicant.phone || "").trim()
                ? t("admin.applicants.dialog.sections.external_applicant")
                : t("admin.applicants.dialog.sections.internal_applicant")
            }
            variant="outlined"
            color={"default"}
            sx={{
              borderColor: "text.secondary",
            }}
          />

          {/* 2. Email Chip */}
          <Chip icon={<Email fontSize="small" />} label={applicant.email} variant="outlined" />

          {/* 3. Phone Chip */}
          <Chip
            icon={<Phone fontSize="small" />}
            label={applicant.phone || t("admin.applicants.card.no_phone")}
            variant="outlined"
          />

          {/* 4. Date Chip */}
          <Chip
            icon={<CalendarToday fontSize="small" />}
            label={`${t("admin.applicants.dialog.sections.applied_date", {
              date: new Date(applicant.submittedAt).toLocaleDateString(),
            })}`}
            variant="outlined"
          />
        </Box>

        <Grid container spacing={4}>
          {/* LEFT COLUMN: Rating & Answers */}
          <Grid item xs={12} md={4}>
            {/* Score Card */}
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "#fff9c4", // Light yellow
                color: "#f9a825", // Darker yellow text
                mb: 3,
                borderColor: "#fbc02d",
              }}
            >
              <Typography variant="overline" color="text.primary" fontWeight="bold">
                {t("admin.applicants.dialog.headers.ai_rating")}
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" mt={1}>
                <Star fontSize="large" sx={{ color: "#fbc02d", mr: 1 }} />
                <Typography variant="h2" color="text.primary" fontWeight="bold">
                  {applicant.rating}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ alignSelf: "flex-end", mb: 1, ml: 0.5 }}
                >
                  / 100
                </Typography>
              </Box>
            </Paper>

            {/* If you have specific metadata or quick stats, they go here */}
          </Grid>

          {/* RIGHT COLUMN: Application Q&A */}
          <Grid item xs={12} md={8}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ borderBottom: 1, borderColor: "divider", pb: 1, mb: 3 }}
            >
              {t("admin.applicants.dialog.headers.app_responses")}
            </Typography>
            {renderQnA()}
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* BOTTOM SECTION: AI Analysis */}
        <Box>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center">
            <AutoAwesome color="primary" sx={{ mr: 1 }} />{" "}
            {t("admin.applicants.dialog.headers.ai_analysis")}
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Strengths List */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: "100%", borderColor: "success.main", bgcolor: "#f1f8e9" }}
              >
                <Typography
                  variant="subtitle1"
                  color="success.main"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  gutterBottom
                >
                  <ThumbUp fontSize="small" sx={{ mr: 1 }} />{" "}
                  {t("admin.applicants.dialog.sections.strengths")}
                </Typography>
                <List dense>
                  {strengthsList.length > 0 ? (
                    strengthsList.map((str, index) => (
                      <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                          <Circle sx={{ fontSize: 8, color: "success.main" }} />
                        </ListItemIcon>
                        <ListItemText primary={str} />
                      </ListItem>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.applicants.dialog.sections.no_strengths")}
                    </Typography>
                  )}
                </List>
              </Paper>
            </Grid>

            {/* Weaknesses List */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: "100%", borderColor: "error.main", bgcolor: "#fbe9e7" }}
              >
                <Typography
                  variant="subtitle1"
                  color="error.main"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  gutterBottom
                >
                  <ThumbDown fontSize="small" sx={{ mr: 1 }} />{" "}
                  {t("admin.applicants.dialog.sections.weaknesses")}
                </Typography>
                <List dense>
                  {weaknessesList.length > 0 ? (
                    weaknessesList.map((wk, index) => (
                      <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                          <Circle sx={{ fontSize: 8, color: "error.main" }} />
                        </ListItemIcon>
                        <ListItemText primary={wk} />
                      </ListItem>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.applicants.dialog.sections.no_weaknesses")}
                    </Typography>
                  )}
                </List>
              </Paper>
            </Grid>

            {/* Full Reasoning Text */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, bgcolor: "grey.100" }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("admin.applicants.dialog.headers.detailed_reasoning")}
                </Typography>
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {renderReasoning(applicant.reasoning)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "grey.50", gap: 1 }}>
        {/* --- LEFT SIDE: Action Buttons --- */}
        <Box display="flex" gap={1} flexGrow={1}>
          {/* 1. DOWNLOAD CV BUTTON */}
          {applicant.hasCv && !downloadLoading ? (
            <MDButton
              variant="contained"
              color="primary"
              startIcon={<Download />}
              onClick={handleDownloadCv}
            >
              {t("admin.applicants.dialog.actions.download_cv")}
            </MDButton>
          ) : applicant.hasCv && downloadLoading ? (
            <Box display="flex" alignItems="center" px={2}>
              <CircularProgress size={24} color="primary" />
            </Box>
          ) : null}

          {/* 2. CHANGE STATUS BUTTON */}
          {!statusLoading && (canEdit || employeeId === empRequestedBy) ? (
            <>
              <MDButton
                variant="outlined"
                color="info"
                startIcon={<Edit />}
                onClick={handleStatusClick}
              >
                {t("admin.applicants.dialog.actions.change_status") || "Change Status"}
              </MDButton>
              <Menu
                anchorEl={statusAnchorEl}
                open={Boolean(statusAnchorEl)}
                onClose={handleStatusClose}
              >
                {statuses
                  .filter((option) => {
                    const isAdmin = employee?.isAdmin === true;
                    const isCeo = employee?.role === 1;
                    const isOwner = employeeId === empRequestedBy;

                    // ROLE 1 → show everything
                    if (isCeo) return true;

                    // ADMIN → hide Accepted
                    if (isAdmin) {
                      return option.value !== "Accepted";
                    }

                    // OWNER → hide Shortlisted
                    if (isOwner) {
                      return option.value !== "Shortlisted";
                    }

                    // default fallback
                    return true;
                  })
                  .map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleChangeStatus(option.value)}
                      selected={applicant.status === option.value}
                      sx={(theme) => ({
                        fontSize: "0.875rem",
                        color: theme.palette[option.color].main,
                        "&.Mui-selected": {
                          color: theme.palette[option.color].main,
                          backgroundColor: "transparent",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      })}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
              </Menu>
            </>
          ) : !statusLoading && !canEdit ? (
            ""
          ) : (
            <Box display="flex" alignItems="center" px={2}>
              <CircularProgress size={24} color="info" />
            </Box>
          )}

          <MDButton
            variant="outlined"
            color="success"
            startIcon={<Event />} // or any meeting icon you prefer
            onClick={handleSetMeeting}
            // disabled={!canEdit} // optional: disable if not editable
            disabled={!(canEdit || employeeId === empRequestedBy)}
          >
            {t("admin.applicants.dialog.actions.set_meeting")}{" "}
            {/* {meetingsCount > 0 &&
              `(t("admin.talentAcquisition.applicants.scheduled") ${meetingsCount})`} */}
            {meetingsCount > 0 && `(${t("admin.applicants.scheduled")}: ${meetingsCount})`}
          </MDButton>

          <Dialog open={calendarOpen} onClose={handleCloseCalendar} maxWidth="md" fullWidth>
            <DialogTitle>{t("admin.talentAcquisition.meetings.meetingDate")}</DialogTitle>
            <DialogContent>
              <CalendarComponent
                onSelectSlot={handleSelectSlot}
                applicantMeetings={filteredMeetingsApplicant}
              />
            </DialogContent>
          </Dialog>

          <TimeDialog
            open={timeDialogOpen}
            onClose={() => setTimeDialogOpen(false)}
            selectedDate={selectedDate}
            employeeId={employeeId} // from ApplicantDialog props
            applicantId={applicant?.id} // directly from applicant object
            // onConfirm={(dateTime) => {
            //   setCalendarOpen(false); // optionally close the calendar after selecting time
            // }}
            onConfirm={(newMeeting) => {
              // Update the state so the calendar shows it
              // setFilteredMeetingsApplicant(prev => [...prev, newMeeting]);
              setMeetingsAll((prev) => [...prev, newMeeting]);
              // setMeetingsCount(prevCount => prevCount + 1);
              // Close dialogs
              // setTimeDialogOpen(false);
              // setCalendarOpen(false);
            }}
          />
        </Box>

        {/* --- RIGHT SIDE: Close Button --- */}
        <MDButton onClick={onClose} color="secondary" variant="text">
          {t("admin.applicants.dialog.actions.close")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

ApplicantDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  applicant: PropTypes.object,
  questions: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  meetings: PropTypes.object,
  employeeId: PropTypes.string,
  allMeetings: PropTypes.object,
  empRequestedBy: PropTypes.string,
};

export default ApplicantDialog;
